import { lezerParser, parse, parseLezer } from '../parser';
import { pathArgCommentRanges } from '../parser/path-args-tokenizer';

import type { SyntaxNode, Tree } from '@lezer/common';

import type { TextDocument } from './document';
import type { Range } from './types';
import type {
  Comment,
  Program,
  Statement,
  Expression,
  PathCommand,
  PathArg,
  FunctionCall,
  MethodCallExpression,
  TernaryExpression,
  BinaryExpression,
  TextBodyItem,
  CasePattern,
  SwitchExpression,
  ArrayDestructuringPattern,
  ObjectDestructuringPattern,
} from '../parser/ast';

export interface FormatOptions {
  /** Indentation string (default: '  ' — two spaces) */
  indent?: string;
}

export interface FormatEdit {
  range: Range;
  newText: string;
}

/**
 * Format a Pathogen document.
 * Returns a single edit that replaces the entire document content.
 * Returns empty array if the source cannot be parsed.
 */
/** Identifier-like words (names, keywords, command letters, words inside strings and comments), counted. */
function wordCounts(text: string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const m of text.matchAll(/[A-Za-z_][A-Za-z0-9_]*/g)) {
    counts.set(m[0], (counts.get(m[0]) ?? 0) + 1);
  }
  return counts;
}

/** True when any word occurs fewer times in `after` than in `before`. */
function dropsWords(before: string, after: string): boolean {
  const was = wordCounts(before);
  const now = wordCounts(after);
  for (const [word, n] of was) {
    if ((now.get(word) ?? 0) < n) return true;
  }
  return false;
}

/**
 * True when the error-recovery parse skipped real text. A missing semicolon is a
 * zero-length error node (an insertion, nothing lost); an unparseable region
 * is an error node with extent, and the recovered AST does not contain it —
 * formatting from that AST would write the file back without it.
 */
function recoveryDroppedText(tree: Tree): boolean {
  const cursor = tree.cursor();
  do {
    if (cursor.type.isError && cursor.to > cursor.from) return true;
  } while (cursor.next());
  return false;
}

// ─── Comments ───────────────────────────────────────────────────────────────
//
// A comment is legal anywhere whitespace is, but the AST only has a place for
// the ones that sit in a list (a statement list, between switch clauses,
// between text-body items) — those are re-emitted on their own line. A
// comment anywhere else (inside an array literal, a call's arguments, a method
// chain) has no AST node, so reprinting its statement from the AST would
// delete it. Such a statement is emitted VERBATIM instead, re-indented as a
// unit: formatting never moves a comment away from the thing it describes,
// and the rest of the document still formats.

/** Every comment in the text, in order. Reads the parse tree, so `//` inside a string or template does not count. */
function commentTexts(text: string): string[] {
  if (!text.includes('//')) return [];
  const found: string[] = [];
  const cursor = lezerParser.parse(text).cursor();
  do {
    if (cursor.name === 'LineComment') {
      found.push(text.slice(cursor.from, cursor.to).trimEnd());
    } else if (cursor.name === 'PathArgs') {
      // A comment inside an open paren of a path argument lives in the
      // token's text, not in the tree.
      const args = text.slice(cursor.from, cursor.to);
      for (const [from, to] of pathArgCommentRanges(args)) found.push(args.slice(from, to).trimEnd());
    } else if (cursor.name === 'StyleContent') {
      // Likewise inside a style block: its content is one raw token, and the
      // style parser treats `//` to end of line as a comment.
      for (const comment of styleContentComments(text.slice(cursor.from, cursor.to))) found.push(comment);
    }
  } while (cursor.next());
  return found;
}

/** The `//…` comments inside a style block's raw content — the rule parseStyleDeclarations applies. */
function styleContentComments(content: string): string[] {
  if (!content.includes('//')) return [];
  return [...content.matchAll(/\/\/[^\n]*/g)].map((m) => m[0].trimEnd());
}

/** True when `after` does not carry every comment of `before`, in order. */
function dropsComments(before: string, after: string): boolean {
  const was = commentTexts(before);
  if (was.length === 0) return false;
  const now = commentTexts(after);
  return was.length !== now.length || was.some((text, i) => text !== now[i]);
}

/** Offsets of the comments the AST carries (Comment nodes anywhere in it). */
function capturedCommentOffsets(node: unknown, into: Set<number>): Set<number> {
  if (Array.isArray(node)) {
    for (const child of node) capturedCommentOffsets(child, into);
  } else if (node && typeof node === 'object') {
    const record = node as { type?: unknown; loc?: { offset?: number } };
    if (record.type === 'Comment' && typeof record.loc?.offset === 'number') into.add(record.loc.offset);
    for (const key in record) {
      if (key !== 'loc') capturedCommentOffsets((record as Record<string, unknown>)[key], into);
    }
  }
  return into;
}

/**
 * Tree nodes whose direct children form a list the AST keeps comments for.
 * Two kinds. BODY-ONLY nodes are never list items themselves (a Block belongs
 * to the statement that owns it). The text-flow nodes are BOTH: their `{ … }`
 * items are their direct children, and they are themselves items of the text
 * body around them — so a comment in the HEADER of `if (… // note⏎) { … }`
 * makes that text-if the verbatim item, not the whole `text() { … }` above it.
 */
const BODY_ONLY_CONTAINERS = new Set([
  'Program',
  'Block',
  'TrailingBlock',
  'PathBlockExpression',
  'TextBlockExpression',
  'TextBlock',
]);
const LIST_CONTAINERS = new Set([
  ...BODY_ONLY_CONTAINERS,
  'TextForLoop',
  'TextForEachLoop',
  'TextIfStatement',
  'TextCaseClause',
  'TextDefaultClause',
]);

/** Verbatim spans, keyed by the start offset the AST statement carries in `loc.offset`. */
interface VerbatimSpan {
  to: number;
  /** Multi-line templates inside the span: their continuation lines are content, never re-indented. */
  templates: { from: number; to: number }[];
}

function verbatimSpans(source: string, ast: Program): Map<number, VerbatimSpan> {
  const spans = new Map<number, VerbatimSpan>();
  if (!source.includes('//')) return spans;
  const captured = capturedCommentOffsets(ast, new Set<number>());
  const tree = lezerParser.parse(source);
  const cursor = tree.cursor();
  do {
    // Three places a comment can be that the AST does not carry: a tree
    // comment outside the lists; one carried inside a PathArgs token; one
    // inside a style block's raw content (printed from parsed declarations).
    let uncaptured = false;
    if (cursor.name === 'LineComment') uncaptured = !captured.has(cursor.from);
    else if (cursor.name === 'PathArgs') {
      uncaptured = pathArgCommentRanges(source.slice(cursor.from, cursor.to)).length > 0;
    } else if (cursor.name === 'StyleContent') {
      uncaptured = styleContentComments(source.slice(cursor.from, cursor.to)).length > 0;
    }
    if (!uncaptured) continue;
    // The innermost node that is an item of a list: climb from the comment's
    // parent until the next step up is a container. (An uncaptured comment can
    // sit directly in a container — an expression-bodied `{|v| … }` — in which
    // case the climb carries on to the statement that holds the container.)
    let item: SyntaxNode | null = cursor.node.parent;
    while (item?.parent && !(LIST_CONTAINERS.has(item.parent.name) && !BODY_ONLY_CONTAINERS.has(item.name))) {
      item = item.parent;
    }
    if (!item?.parent) continue;
    const templates: { from: number; to: number }[] = [];
    item.cursor().iterate((n) => {
      if (n.name === 'TemplateLiteral' && source.slice(n.from, n.to).includes('\n')) {
        templates.push({ from: n.from, to: n.to });
      }
    });
    spans.set(item.from, { to: item.to, templates });
  } while (cursor.next());
  return spans;
}

/** Set for the duration of one formatDocument call (the formatter is synchronous and single-entry). */
let activeVerbatim: { source: string; spans: Map<number, VerbatimSpan> } | null = null;

/**
 * The source text of a statement that must not be reprinted, moved to `prefix`.
 * Continuation lines keep their shape: the statement's original indent is
 * swapped for the new one, a line that did not have it is left alone, and a
 * line that is the inside of a multi-line template is never touched.
 */
function verbatimText(node: object, prefix: string): string | null {
  // Not every statement / text-body item type declares a `loc`
  const offset = (node as { loc?: { offset?: number } }).loc?.offset;
  if (!activeVerbatim || typeof offset !== 'number') return null;
  const span = activeVerbatim.spans.get(offset);
  if (!span) return null;
  const { source } = activeVerbatim;
  const lineStart = source.lastIndexOf('\n', offset - 1) + 1;
  const lead = source.slice(lineStart, offset);
  const originalIndent = /^[ \t]*$/.test(lead) ? lead : null;
  let position = offset;
  return source
    .slice(offset, span.to)
    .split('\n')
    .map((line, i) => {
      const at = position;
      position += line.length + 1;
      if (i === 0) return prefix + line;
      if (originalIndent === null || span.templates.some((t) => at > t.from && at <= t.to)) return line;
      return line.startsWith(originalIndent) ? prefix + line.slice(originalIndent.length) : line;
    })
    .join('\n');
}

/**
 * True when a comment shares its source line with code before it
 * (`let x = 50; // note`). Such a comment describes that line, so it stays at
 * the end of it — printed on a line of its own it would sit directly above
 * the NEXT statement and read as describing that instead.
 */
function trailsCode(comment: { loc?: { offset?: number } }, source?: string): boolean {
  const offset = comment.loc?.offset;
  if (!source || typeof offset !== 'number') return false;
  const lineStart = source.lastIndexOf('\n', offset - 1) + 1;
  return source.slice(lineStart, offset).trim() !== '';
}

/** Add a comment to a list being printed: at the end of the previous entry when it trails code, else on its own line. */
function pushComment(lines: string[], comment: Comment, prefix: string, source?: string): void {
  if (lines.length > 0 && lines[lines.length - 1] !== '' && trailsCode(comment, source)) {
    lines[lines.length - 1] += ` ${comment.text}`;
  } else {
    lines.push(`${prefix}${comment.text}`);
  }
}

/** Print the items of a text body, keeping a trailing comment on its item's line. */
function formatTextBodyItems(items: TextBodyItem[], depth: number, indent: string, source?: string): string {
  const lines: string[] = [];
  for (const item of items) {
    if (item.type === 'Comment') pushComment(lines, item, indent.repeat(depth), source);
    else lines.push(formatTextBodyItem(item, depth, indent, source));
  }
  return lines.join('\n');
}

export function formatDocument(document: TextDocument, options?: FormatOptions): FormatEdit[] {
  const source = document.getText();
  const indent = options?.indent ?? '  ';

  let ast: Program;
  try {
    // Naming mistakes (a reassigned const, `let rad`) don't change the
    // formatting; diagnostics reports them.
    ast = parse(source, { namingChecks: false });
  } catch {
    // parse() is strict — it rejects code with missing semicolons.
    // Fall back to Lezer's error-recovery parse so the formatter can still
    // work on code that has minor issues (like missing semicolons).
    try {
      const lezerResult = parseLezer(source);
      // Never write back a recovered parse that lost code (a doubled
      // `#{{ ... }}` once became an empty style block on format).
      if (recoveryDroppedText(lezerResult.tree)) return [];
      ast = lezerResult.ast;
    } catch {
      return []; // Can't format at all
    }
  }

  let raw: string;
  activeVerbatim = { source, spans: verbatimSpans(source, ast) };
  try {
    raw = formatStatements(ast.body, 0, indent, source);
  } finally {
    activeVerbatim = null;
  }

  // Strip trailing whitespace on every line
  const stripped = raw.split('\n').map((line) => line.trimEnd()).join('\n');
  // Preserve final newline if the source had one
  const sourceEndsWithNewline = source.endsWith('\n');
  const formatted = sourceEndsWithNewline && !stripped.endsWith('\n') ? stripped + '\n' : stripped;

  // If already formatted, return no edits
  if (formatted === source) return [];

  // Formatting moves punctuation and whitespace; it never loses a word. A
  // recovered parse can turn `return @{ l a.x … }` into `return null` with
  // no error node to show for it, so the last line of defence is the text
  // itself: every identifier-like word in the source must still be there.
  if (dropsWords(source, formatted)) return [];
  // …and never loses, reorders or rewrites a comment (one made only of
  // punctuation — `// ----` — has no word for the check above to miss).
  if (dropsComments(source, formatted)) return [];

  return [{
    range: {
      start: { line: 0, character: 0 },
      end: document.positionAt(source.length),
    },
    newText: formatted,
  }];
}

// --- Blank line helpers ---

function countBlankLinesBetween(source: string, fromOffset: number, toOffset: number): number {
  const segment = source.slice(fromOffset, toOffset);
  const lines = segment.split('\n');
  // Skip first line (belongs to previous stmt) and last line (belongs to current stmt)
  let blanks = 0;
  for (let i = 1; i < lines.length - 1; i++) {
    if (lines[i].trim() === '') blanks++;
  }
  return blanks;
}

// --- Formatting ---

function formatStatements(stmts: Statement[], depth: number, indent: string, source?: string): string {
  const lines: string[] = [];
  const prefix = indent.repeat(depth);

  for (let i = 0; i < stmts.length; i++) {
    const stmt = stmts[i];

    // Preserve blank lines from source, normalize 3+ → 2
    if (source && i > 0) {
      const prevLoc = (stmts[i - 1] as any).loc;
      const currLoc = (stmt as any).loc;
      if (prevLoc?.offset != null && currLoc?.offset != null) {
        const blanks = countBlankLinesBetween(source, prevLoc.offset, currLoc.offset);
        const normalized = Math.min(blanks, 2);
        for (let j = 0; j < normalized; j++) {
          lines.push('');
        }
      }
    }

    // Text-form if/for/switch bodies are TextBodyItem[] cast to Statement[]
    // (see TextIfStatement in the AST builder); tspan and bare template
    // items must go through the text-body printer or they fall into the
    // `default` branch below and vanish from the output.
    const kind = (stmt as { type: string }).type;
    if (kind === 'TspanStatement' || kind === 'TemplateLiteral') {
      lines.push(formatTextBodyItem(stmt as unknown as TextBodyItem, depth, indent, source));
      continue;
    }

    if (stmt.type === 'Comment') {
      pushComment(lines, stmt, prefix, source);
      continue;
    }

    lines.push(formatStatement(stmt, depth, indent, prefix, source));
  }

  return lines.join('\n');
}

function formatStatement(stmt: Statement, depth: number, indent: string, prefix: string, source?: string): string {
  const verbatim = verbatimText(stmt, prefix);
  if (verbatim !== null) return verbatim;
  switch (stmt.type) {
    case 'LetDeclaration': {
      // kind is `let` or `const`; printing a literal `let` would turn every const into a let.
      const value = formatExpression(stmt.value, depth, indent, source);
      if (stmt.pattern) {
        return `${prefix}${stmt.kind} ${formatDestructuringPattern(stmt.pattern)} = ${value};`;
      }
      return `${prefix}${stmt.kind} ${stmt.name} = ${value};`;
    }
    case 'AssignmentStatement':
      return `${prefix}${stmt.name} = ${formatExpression(stmt.value, depth, indent, source)};`;
    case 'FunctionDefinition': {
      const paramStr = formatParamList(stmt.params, depth, indent);
      const body = formatStatements(stmt.body, depth + 1, indent, source);
      return `${prefix}fn ${stmt.name}(${paramStr}) {\n${body}\n${prefix}}`;
    }
    case 'EnumDefinition': {
      const members = stmt.members.map((m) => {
        const val = m.value ? ` = ${formatExpression(m.value, depth + 1, indent, source)}` : '';
        return `${indent.repeat(depth + 1)}${m.name}${val},`;
      }).join('\n');
      return `${prefix}enum ${stmt.name} {\n${members}\n${prefix}}`;
    }
    case 'ForLoop': {
      const start = formatExpression(stmt.start, depth, indent, source);
      const end = formatExpression(stmt.end, depth, indent, source);
      const body = formatStatements(stmt.body, depth + 1, indent, source);
      const op = stmt.inclusive === false ? '..<' : '..';
      return `${prefix}for (${stmt.variable} in ${start}${op}${end}) {\n${body}\n${prefix}}`;
    }
    case 'ForEachLoop': {
      const iterable = formatExpression(stmt.iterable, depth, indent, source);
      const varName = stmt.indexVariable
        ? `[${stmt.variable}, ${stmt.indexVariable}]`
        : stmt.variable;
      const body = formatStatements(stmt.body, depth + 1, indent, source);
      return `${prefix}for (${varName} in ${iterable}) {\n${body}\n${prefix}}`;
    }
    case 'IfStatement': {
      const condition = formatExpression(stmt.condition, depth, indent, source);
      const consequent = formatStatements(stmt.consequent, depth + 1, indent, source);
      let result = `${prefix}if (${condition}) {\n${consequent}\n${prefix}}`;
      if (stmt.alternate) {
        // `else if` parses as an alternate holding exactly one IfStatement;
        // print it back as the chain the author wrote, not a nested block.
        const only = stmt.alternate.length === 1 ? stmt.alternate[0] : null;
        if (only && only.type === 'IfStatement') {
          result += ` else ${formatStatement(only, depth, indent, prefix, source).slice(prefix.length)}`;
        } else {
          const alternate = formatStatements(stmt.alternate, depth + 1, indent, source);
          result += ` else {\n${alternate}\n${prefix}}`;
        }
      }
      return result;
    }
    case 'PathCommand': {
      const formatted = formatPathCommand(stmt, depth, indent, source);
      // Function calls and method calls wrapped as PathCommand (no command letter)
      // need semicolons — the parser requires them inside .apply blocks and at
      // statement level. Only actual SVG path commands (M, L, C, Z, etc.) skip them.
      const isStatementCall = !stmt.command && stmt.args.length > 0 &&
        (stmt.args[0].type === 'FunctionCall' || stmt.args[0].type === 'MethodCallExpression');
      // Commands carrying suffix clauses (`with fillet(...)` / `as segment(...)`)
      // also take a trailing semicolon so the clauses terminate cleanly — EXCEPT
      // close-path (`z`/`Z`), where the suffix is unambiguous without the
      // terminator, so we emit the terser form. (An earlier GLR ambiguity made
      // `z <suffix>;` mid-document unparseable; fixed via @dynamicPrecedence on
      // PathCommand and pinned by parser.test.ts regressions — this branch is
      // now purely stylistic and safe either way.)
      const isClosePath = stmt.command === 'z' || stmt.command === 'Z';
      const needsSemicolon = isStatementCall || (stmt.annotations != null && !isClosePath);
      return `${prefix}${formatted}${needsSemicolon ? ';' : ''}`;
    }
    case 'ReturnStatement':
      return `${prefix}return ${formatExpression(stmt.value, depth, indent, source)};`;
    case 'BreakStatement':
      return `${prefix}break;`;
    case 'ContinueStatement':
      return `${prefix}continue;`;
    case 'ExpressionStatement':
      return `${prefix}${formatExpression(stmt.expression, depth, indent, source)};`;
    case 'LayerDefinition': {
      const name = formatExpression(stmt.name, depth, indent, source);
      const style = formatExpression(stmt.styleExpr, depth, indent, source);
      const def = stmt.isDefault ? 'default ' : '';
      return `${prefix}define ${def}${stmt.layerType}(${name}) ${style}`;
    }
    case 'ViewBoxDefinition': {
      const ox = formatExpression(stmt.originX, depth, indent, source);
      const oy = formatExpression(stmt.originY, depth, indent, source);
      const w = formatExpression(stmt.width, depth, indent, source);
      const h = formatExpression(stmt.height, depth, indent, source);
      return `${prefix}define ViewBox(${ox}, ${oy}, ${w}, ${h});`;
    }
    case 'LayerApplyBlock': {
      const body = formatStatements(stmt.body, depth + 1, indent, source);
      if (stmt.layerName.type === 'Identifier') {
        const name = formatExpression(stmt.layerName, depth, indent, source);
        return `${prefix}${name}.apply {\n${body}\n${prefix}}`;
      }
      const name = formatExpression(stmt.layerName, depth, indent, source);
      return `${prefix}layer(${name}).apply {\n${body}\n${prefix}}`;
    }
    case 'TextStatement': {
      const args: string[] = [
        formatExpression(stmt.x, depth, indent, source),
        formatExpression(stmt.y, depth, indent, source),
      ];
      if (stmt.rotation) args.push(formatExpression(stmt.rotation, depth, indent, source));
      if (stmt.styles) args.push(formatExpression(stmt.styles, depth, indent, source));
      if (stmt.content) {
        return `${prefix}text(${args.join(', ')})${formatExpression(stmt.content, depth, indent, source)};`;
      }
      if (stmt.body) {
        const body = formatTextBodyItems(stmt.body, depth + 1, indent, source);
        return `${prefix}text(${args.join(', ')}) {\n${body}\n${prefix}}`;
      }
      return `${prefix}text(${args.join(', ')});`;
    }
    case 'Comment':
      return `${prefix}${stmt.text}`;
    case 'IndexedAssignmentStatement':
      return `${prefix}${formatExpression(stmt.object, depth, indent, source)}[${formatExpression(stmt.index, depth, indent, source)}] = ${formatExpression(stmt.value, depth, indent, source)};`;
    case 'MemberAssignmentStatement':
      return `${prefix}${formatExpression(stmt.object, depth, indent, source)}.${stmt.property} = ${formatExpression(stmt.value, depth, indent, source)};`;
    case 'FontDirective': {
      const src = stmt.sourceKind === 'identifier' ? stmt.source : `"${stmt.source}"`;
      return `${prefix}@font ${src}${stmt.weight ? ` ${stmt.weight}` : ''};`;
    }
    case 'SwitchStatement': {
      const disc = formatExpression(stmt.discriminant, depth, indent, source);
      const inner = indent.repeat(depth + 1);
      const clauses: string[] = [];
      // Comments between clauses — usually a clause that was commented out —
      // are re-indented with the clauses they sit among.
      const pushComments = (comments?: Comment[]): void => {
        for (const comment of comments ?? []) pushComment(clauses, comment, inner, source);
      };
      for (const c of stmt.cases) {
        pushComments(c.leadingComments);
        const patterns = c.patterns.map((p) => formatCasePattern(p, depth + 1, indent, source)).join(', ');
        const guard = c.guard ? ` where ${formatExpression(c.guard, depth + 1, indent, source)}` : '';
        const body = formatStatements(c.body, depth + 2, indent, source);
        clauses.push(`${inner}case ${patterns}${guard} {\n${body}\n${inner}}`);
      }
      if (stmt.defaultCase) {
        pushComments(stmt.defaultCase.leadingComments);
        const body = formatStatements(stmt.defaultCase.body, depth + 2, indent, source);
        clauses.push(`${inner}default {\n${body}\n${inner}}`);
      }
      pushComments(stmt.trailingComments);
      return `${prefix}switch(${disc}) {\n${clauses.join('\n')}\n${prefix}}`;
    }
    default:
      return prefix;
  }
}

// --- Destructuring + case patterns ---

/** `[a, b, ...rest]` / `{ x, y: alias, ...rest }` — shared by `let` and `case`. */
function formatDestructuringPattern(pattern: ArrayDestructuringPattern | ObjectDestructuringPattern): string {
  if (pattern.type === 'ArrayDestructuringPattern') {
    const elements = pattern.elements.join(', ');
    const rest = pattern.rest ? `, ...${pattern.rest}` : '';
    return `[${elements}${rest}]`;
  }
  const props = pattern.properties.map((p) =>
    p.alias ? `${p.key}: ${p.alias}` : p.key,
  ).join(', ');
  const rest = pattern.rest ? `, ...${pattern.rest}` : '';
  return `{ ${props}${rest} }`;
}

// Always multi-line, one arm per line, arms indented one level past the
// line the expression starts on (the closing brace lines up with that line).
// `switch(` hugs its parenthesis like a call — the canonical spelling in the
// docs — while `if (` / `for (` keep their space.
function formatSwitchExpression(expr: SwitchExpression, depth: number, indent: string, source?: string): string {
  const disc = formatExpression(expr.discriminant, depth, indent, source);
  const inner = indent.repeat(depth + 1);
  const arms = expr.arms.map((arm) => {
    const patterns = arm.patterns.map((p) => formatCasePattern(p, depth + 1, indent, source)).join(', ');
    const guard = arm.guard ? ` where ${formatExpression(arm.guard, depth + 1, indent, source)}` : '';
    return `${inner}case ${patterns}${guard} { ${formatExpression(arm.value, depth + 1, indent, source)} }`;
  });
  arms.push(`${inner}default { ${formatExpression(expr.defaultValue, depth + 1, indent, source)} }`);
  return `switch(${disc}) {\n${arms.join('\n')}\n${indent.repeat(depth)}}`;
}

function formatCasePattern(pattern: CasePattern, depth: number, indent: string, source?: string): string {
  switch (pattern.type) {
    case 'ValuePattern':
      return formatExpression(pattern.value, depth, indent, source);
    case 'RangePattern': {
      const start = pattern.start ? formatExpression(pattern.start, depth, indent, source) : '';
      const end = pattern.end ? formatExpression(pattern.end, depth, indent, source) : '';
      return `${start}${pattern.inclusive ? '..' : '..<'}${end}`;
    }
    case 'ArrayDestructuringPattern':
    case 'ObjectDestructuringPattern':
      return formatDestructuringPattern(pattern);
  }
}

// --- Text body item formatting ---

function formatTextBodyItem(item: TextBodyItem, depth: number, indent: string, source?: string): string {
  const prefix = indent.repeat(depth);
  const verbatim = verbatimText(item, prefix);
  if (verbatim !== null) return verbatim;
  if (item.type === 'TspanStatement') {
    const args: string[] = [];
    if (item.dx) args.push(formatExpression(item.dx, depth, indent, source));
    if (item.dy) args.push(formatExpression(item.dy, depth, indent, source));
    if (item.rotation) args.push(formatExpression(item.rotation, depth, indent, source));
    if (item.styles) args.push(formatExpression(item.styles, depth, indent, source));
    const content = formatExpression(item.content, depth, indent, source);
    return `${prefix}tspan(${args.join(', ')})${content};`;
  }
  if (item.type === 'TemplateLiteral') {
    // A bare template item takes no semicolon: the text-body grammar has
    // none for it, so emitting one turned a valid file into a parse error.
    return `${prefix}${formatExpression(item, depth, indent, source)}`;
  }
  // ForLoop, ForEachLoop, IfStatement, SwitchStatement, LetDeclaration are Statement types
  return formatStatement(item as Statement, depth, indent, prefix, source);
}

// --- Function param wrapping ---

function formatParamList(params: string[], depth: number, indent: string): string {
  if (params.length < 5) return params.join(', ');
  // Break after every 4th param, 4-space continuation indent
  const contIndent = indent.repeat(depth) + '    ';
  const chunks: string[][] = [];
  for (let i = 0; i < params.length; i += 4) {
    chunks.push(params.slice(i, i + 4));
  }
  return chunks.map((chunk, i) =>
    i === 0 ? chunk.join(', ') : `${contIndent}${chunk.join(', ')}`,
  ).join(',\n');
}

// --- Path commands ---

function formatPathCommand(cmd: PathCommand, depth: number, indent: string, source?: string): string {
  const suffix = formatPathAnnotations(cmd.annotations, depth, indent, source);
  if (cmd.args.length === 0) return `${cmd.command}${suffix}`;
  const args = cmd.args.map((a) => formatPathArg(a, depth, indent, source)).join(' ');
  // Empty command (e.g., standalone function call like circle(cx, cy, r))
  if (!cmd.command) return `${args}${suffix}`;
  return `${cmd.command} ${args}${suffix}`;
}

// `with <cornerOp>(...)` then comma-separated `as segment('x'), endpoint('y')`.
// Order is fixed (with before as) to match the grammar.
function formatPathAnnotations(
  annotations: PathCommand['annotations'],
  depth: number,
  indent: string,
  source?: string,
): string {
  if (!annotations) return '';
  let result = '';
  if (annotations.cornerOp) {
    const args = annotations.cornerOp.args
      .map((a) => formatExpression(a, depth, indent, source))
      .join(', ');
    result += ` with ${annotations.cornerOp.kind}(${args})`;
  }
  if (annotations.labels && annotations.labels.length > 0) {
    const labels = annotations.labels
      .map((l) => `${l.kind}(${formatExpression(l.name, depth, indent, source)})`)
      .join(', ');
    result += ` as ${labels}`;
  }
  return result;
}

function formatPathArg(arg: PathArg, depth: number, indent: string, source?: string): string {
  switch (arg.type) {
    case 'NumberLiteral': {
      const unit = arg.unit ?? '';
      return `${arg.value}${unit}`;
    }
    case 'BooleanLiteral':
      return arg.value ? '1' : '0';
    case 'Identifier':
      return arg.name;
    case 'CalcExpression':
      return `calc(${formatExpression(arg.expression, depth, indent, source)})`;
    case 'FunctionCall':
      return formatFunctionCallExpr(arg as FunctionCall, depth, indent, source);
    case 'MemberExpression':
      return formatExpression(arg as any, depth, indent, source);
    case 'IndexExpression':
      return `${formatExpression((arg as any).object, depth, indent, source)}[${formatExpression((arg as any).index, depth, indent, source)}]`;
    case 'MethodCallExpression':
      return formatExpression(arg as any, depth, indent, source);
    default:
      return '';
  }
}

// --- Method chain helpers ---

function countMethodChainDepth(expr: Expression): number {
  let count = 0;
  let current: Expression = expr;
  while (current.type === 'MethodCallExpression') {
    count++;
    current = (current as MethodCallExpression).object;
  }
  return count;
}

function formatMethodChain(expr: MethodCallExpression, depth: number, indent: string, source?: string): string {
  // Collect chain steps from innermost to outermost
  const steps: { method: string; args: Expression[]; block?: { params: string[]; body: Statement[] } }[] = [];
  let base: Expression = expr;
  while (base.type === 'MethodCallExpression') {
    const mce = base as MethodCallExpression;
    steps.unshift({ method: mce.method, args: mce.args, block: mce.block });
    base = mce.object;
  }

  const baseStr = formatExpression(base, depth, indent, source);
  const contIndent = indent.repeat(depth) + '    ';

  let result = baseStr;
  for (const step of steps) {
    const argStr = formatArgList(step.args, depth, indent, source);
    const blockStr = step.block ? formatTrailingBlock(step.block, depth, indent, source) : '';
    result += `\n${contIndent}.${step.method}(${argStr})${blockStr}`;
  }
  return result;
}

// --- Trailing block ---

function formatTrailingBlock(
  block: { params: string[]; body: Statement[] },
  depth: number,
  indent: string,
  source?: string,
): string {
  const paramStr = block.params.length > 0 ? `|${block.params.join(', ')}|` : '';
  if (block.body.length === 0) {
    return ` {${paramStr}}`;
  }
  // Expression-bodied sugar {|v| v * 2}: a single implicit return prints
  // back as the bare expression, inline.
  const only = block.body.length === 1 ? block.body[0] : null;
  if (only && only.type === 'ReturnStatement' && only.implicit) {
    return ` {${paramStr} ${formatExpression(only.value, depth, indent, source)}}`;
  }
  const bodyLines = formatStatements(block.body, depth + 1, indent, source);

  // Attempt gradient stop column alignment
  const aligned = alignGradientStops(bodyLines, indent.repeat(depth + 1));

  return ` {${paramStr}\n${aligned}\n${indent.repeat(depth)}}`;
}

// --- Gradient stop alignment ---

function alignGradientStops(bodyText: string, _innerIndent: string): string {
  const lines = bodyText.split('\n');
  // Check if all non-empty lines match the stop pattern
  const stopPattern = /^(\s*\S+\.stop\()([^,]+)(,\s*.+);$/;
  const matches = lines.map((line) => line.trim() === '' ? null : stopPattern.exec(line));
  const allStops = matches.every((m, i) => m !== null || lines[i].trim() === '');

  if (!allStops || matches.filter(Boolean).length < 2) return bodyText;

  const maxOffsetWidth = Math.max(...matches.map((m) => m ? m[2].length : 0));

  return lines.map((line, i) => {
    const m = matches[i];
    if (!m) return line;
    const padded = m[2].padEnd(maxOffsetWidth);
    return `${m[1]}${padded}${m[3]};`;
  }).join('\n');
}

// --- Function call formatting ---

function shouldWrapArgs(args: Expression[]): boolean {
  // Style guide: wrap if 5+ args
  if (args.length >= 5) return true;
  // Also wrap if 4 args and any is a complex expression (calc, function call, method call)
  if (args.length >= 4) {
    return args.some((a) =>
      a.type === 'FunctionCall' || a.type === 'CalcExpression' || a.type === 'MethodCallExpression',
    );
  }
  return false;
}

function formatArgList(args: Expression[], depth: number, indent: string, source?: string): string {
  return args.map((a) => formatExpression(a, depth, indent, source)).join(', ');
}

function formatFunctionCallExpr(
  expr: FunctionCall,
  depth: number,
  indent: string,
  source?: string,
): string {
  const args = expr.args.map((a) => formatExpression(a, depth, indent, source));

  let result: string;
  if (shouldWrapArgs(expr.args) && expr.args.length > 0) {
    const contIndent = indent.repeat(depth) + '    ';
    const wrapped = args.map((a, i) =>
      i === 0 ? a : `${contIndent}${a}`,
    );
    result = `${expr.name}(${wrapped[0]},\n${wrapped.slice(1).join(',\n')})`;
  } else {
    result = `${expr.name}(${args.join(', ')})`;
  }

  if (expr.block) {
    result += formatTrailingBlock(expr.block, depth, indent, source);
  }

  return result;
}

// --- Ternary helpers ---

function countLogicalOps(expr: Expression): number {
  if (expr.type !== 'BinaryExpression') return 0;
  const be = expr as BinaryExpression;
  if (be.operator === '||' || be.operator === '&&') {
    return 1 + countLogicalOps(be.left) + countLogicalOps(be.right);
  }
  return 0;
}

function formatTernary(expr: TernaryExpression, depth: number, indent: string, source?: string): string {
  const cond = formatExpression(expr.condition, depth, indent, source);
  const cons = formatExpression(expr.consequent, depth, indent, source);
  const alt = formatExpression(expr.alternate, depth, indent, source);

  const isNestedAlternate = expr.alternate.type === 'TernaryExpression';

  if (isNestedAlternate) {
    const contIndent = indent.repeat(depth + 1);
    const altFormatted = formatExpression(expr.alternate, depth + 1, indent, source);
    return `${cond} ?\n${contIndent}${cons} : ${altFormatted}`;
  }

  const logicalOpCount = countLogicalOps(expr.condition);
  if (logicalOpCount >= 3) {
    const contIndent = indent.repeat(depth + 1);
    // Break the condition at top-level || or && operators
    const condParts = splitAtLogicalOps(expr.condition, depth, indent, source);
    const firstPart = condParts[0];
    const restParts = condParts.slice(1).map((p) => `${contIndent}${p}`);
    const allParts = [firstPart, ...restParts];
    return `${allParts.join('\n')} ? ${cons} : ${alt}`;
  }

  return `${cond} ? ${cons} : ${alt}`;
}

function splitAtLogicalOps(expr: Expression, depth: number, indent: string, source?: string): string[] {
  if (expr.type !== 'BinaryExpression') return [formatExpression(expr, depth, indent, source)];
  const be = expr as BinaryExpression;
  if (be.operator === '||' || be.operator === '&&') {
    const leftParts = splitAtLogicalOps(be.left, depth, indent, source);
    const rightParts = splitAtLogicalOps(be.right, depth, indent, source);
    // Append operator to last part of left
    leftParts[leftParts.length - 1] += ` ${be.operator}`;
    return [...leftParts, ...rightParts];
  }
  return [formatExpression(expr, depth, indent, source)];
}

// --- Expression formatting ---

/**
 * Binary operator precedence, mirroring pathogen.grammar's @precedence block
 * (times > plus > compare > merge > equality > and > or; all left-associative).
 * Higher number binds tighter.
 */
const OP_PRECEDENCE: Record<string, number> = {
  '||': 1,
  '&&': 2,
  '==': 3,
  '!=': 3,
  '<<': 4,
  '<': 5,
  '<=': 5,
  '>': 5,
  '>=': 5,
  '+': 6,
  '-': 6,
  '*': 7,
  '/': 7,
  '%': 7,
};

/** Operators where (a op b) op c === a op (b op c) — equal-precedence right
 * children need no parens. `<<` is deliberately NOT here: worker application
 * makes it non-associative — `a << (vo() << f)` applies f to the builder,
 * while the re-grouped `(a << vo()) << f` evaluates the bare builder call
 * and errors. User-written parens on << right children must survive. */
const ASSOCIATIVE_OPS = new Set(['+', '*', '&&', '||']);

/**
 * Format one operand of a binary expression, parenthesizing when required to
 * preserve the parse. Dropping these parens silently changes semantics —
 * `(i + 0.5) / 28` is NOT `i + 0.5 / 28`.
 */
function formatBinaryOperand(
  child: Expression,
  parentOp: string,
  isRight: boolean,
  depth: number,
  indent: string,
  source?: string,
): string {
  const text = formatExpression(child, depth, indent, source);
  if (child.type === 'TernaryExpression') return `(${text})`;
  if (child.type !== 'BinaryExpression') return text;
  const childPrec = OP_PRECEDENCE[(child as BinaryExpression).operator] ?? 0;
  const parentPrec = OP_PRECEDENCE[parentOp] ?? 0;
  if (childPrec < parentPrec) return `(${text})`;
  // Left-associative grammar: an equal-precedence RIGHT child re-parses as the
  // parent's left unless the operator is associative (a - (b - c), a / (b * c)).
  // `%` is the exception at the times level: a * (b % c) is NOT (a * b) % c
  // (6 * (3 % 2) is 6, 6 * 3 % 2 is 0), so a `%` right child always keeps its
  // parens even under the associative `*`.
  const childOp = (child as BinaryExpression).operator;
  const regroupSafe = ASSOCIATIVE_OPS.has(parentOp) && childOp !== '%';
  if (isRight && childPrec === parentPrec && !regroupSafe) return `(${text})`;
  return text;
}

/**
 * Re-quote a string literal, choosing the quote that minimizes escaping and
 * re-escaping what parseStringContent unescapes. Naive `'${value}'` corrupts
 * any value containing a single quote.
 */
function formatStringValue(value: string): string {
  const escapeCommon = (s: string) =>
    s.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/\t/g, '\\t').replace(/\r/g, '\\r');
  if (value.includes("'") && !value.includes('"')) {
    return `"${escapeCommon(value)}"`;
  }
  return `'${escapeCommon(value).replace(/'/g, "\\'")}'`;
}

function formatExpression(expr: Expression, depth: number, indent: string, source?: string): string {
  switch (expr.type) {
    case 'NumberLiteral': {
      const unit = expr.unit ?? '';
      return `${expr.value}${unit}`;
    }
    case 'StringLiteral':
      return formatStringValue(expr.value);
    case 'TemplateLiteral': {
      const parts = expr.parts.map((p) =>
        typeof p === 'string' ? p : `\${${formatExpression(p, depth, indent, source)}}`,
      );
      return `\`${parts.join('')}\``;
    }
    case 'BooleanLiteral':
      return expr.value ? 'true' : 'false';
    case 'NullLiteral':
      return 'null';
    case 'Identifier':
      return expr.name;
    case 'LambdaExpression': {
      // Zero-param lambdas MUST print as {|| ...} — a bare {} would reparse
      // as an ObjectLiteral.
      const paramStr = expr.params.length > 0 ? `|${expr.params.join(', ')}|` : '||';
      if (expr.body.length === 0) return `{${paramStr}}`;
      // Expression-bodied sugar {|v| v * 2}: a single implicit return
      // prints back as the bare expression, inline.
      const soleStmt = expr.body.length === 1 ? expr.body[0] : null;
      if (soleStmt && soleStmt.type === 'ReturnStatement' && soleStmt.implicit) {
        return `{${paramStr} ${formatExpression(soleStmt.value, depth, indent, source)}}`;
      }
      const bodyLines = formatStatements(expr.body, depth + 1, indent, source);
      return `{${paramStr}\n${bodyLines}\n${indent.repeat(depth)}}`;
    }
    case 'ColorLiteral':
      return expr.raw;
    case 'BinaryExpression': {
      const left = formatBinaryOperand(expr.left, expr.operator, false, depth, indent, source);
      const right = formatBinaryOperand(expr.right, expr.operator, true, depth, indent, source);
      return `${left} ${expr.operator} ${right}`;
    }
    case 'UnaryExpression': {
      const arg = formatExpression(expr.argument, depth, indent, source);
      // -(a + b) must not print as -a + b.
      if (expr.argument.type === 'BinaryExpression' || expr.argument.type === 'TernaryExpression') {
        return `${expr.operator}(${arg})`;
      }
      return `${expr.operator}${arg}`;
    }
    case 'RangeExpression': {
      // The parentheses are part of the literal, so the range prints its own —
      // no spaces around the operator, matching for headers and case arms.
      const start = formatExpression(expr.start, depth, indent, source);
      const end = formatExpression(expr.end, depth, indent, source);
      return `(${start}${expr.inclusive ? '..' : '..<'}${end})`;
    }
    case 'TernaryExpression':
      return formatTernary(expr, depth, indent, source);
    case 'SwitchExpression':
      return formatSwitchExpression(expr, depth, indent, source);
    case 'CalcExpression':
      return `calc(${formatExpression(expr.expression, depth, indent, source)})`;
    case 'FunctionCall':
      return formatFunctionCallExpr(expr, depth, indent, source);
    case 'MethodCallExpression': {
      const chainDepth = countMethodChainDepth(expr);
      if (chainDepth >= 3) {
        return formatMethodChain(expr, depth, indent, source);
      }
      const obj = formatExpression(expr.object, depth, indent, source);
      const args = expr.args.map((a) => formatExpression(a, depth, indent, source));
      let argStr: string;
      if (shouldWrapArgs(expr.args) && expr.args.length > 0) {
        const contIndent = indent.repeat(depth) + '    ';
        const wrapped = args.map((a, i) =>
          i === 0 ? a : `${contIndent}${a}`,
        );
        argStr = `${wrapped[0]},\n${wrapped.slice(1).join(',\n')}`;
      } else {
        argStr = args.join(', ');
      }
      let result = `${obj}.${expr.method}(${argStr})`;
      if (expr.block) {
        result += formatTrailingBlock(expr.block, depth, indent, source);
      }
      return result;
    }
    case 'MemberExpression':
      return `${formatExpression(expr.object, depth, indent, source)}.${expr.property}`;
    case 'IndexExpression':
      return `${formatExpression(expr.object, depth, indent, source)}[${formatExpression(expr.index, depth, indent, source)}]`;
    case 'ArrayLiteral': {
      if (expr.elements.length === 0) return '[]';
      const inner = indent.repeat(depth + 1);
      const close = indent.repeat(depth);
      const elements = expr.elements.map((e) => {
        const formatted = e.type === 'SpreadElement'
          ? `...${formatExpression(e.argument, depth + 1, indent, source)}`
          : formatExpression(e, depth + 1, indent, source);
        return `${inner}${formatted},`;
      });
      return `[\n${elements.join('\n')}\n${close}]`;
    }
    case 'ObjectLiteral': {
      if (expr.properties.length === 0) return '{}';
      const inner = indent.repeat(depth + 1);
      const close = indent.repeat(depth);
      const props = expr.properties.map((p) => {
        if (p.type === 'SpreadElement') {
          return `${inner}...${formatExpression(p.argument, depth + 1, indent, source)},`;
        }
        if (p.shorthand && p.value.type === 'Identifier' && p.value.name === p.key) {
          return `${inner}${p.key},`;
        }
        return `${inner}${p.key}: ${formatExpression(p.value, depth + 1, indent, source)},`;
      });
      return `{\n${props.join('\n')}\n${close}}`;
    }
    case 'StyleBlockLiteral': {
      if (expr.properties.length === 0) return '#{}';
      const inner = indent.repeat(depth + 1);
      const close = indent.repeat(depth);
      const props = expr.properties.map((p) => `${inner}${p.name}: ${p.value};`);
      return `#{\n${props.join('\n')}\n${close}}`;
    }
    case 'LayerConstructorExpression': {
      const name = formatExpression(expr.name, depth, indent, source);
      const style = expr.styleExpr ? ` ${formatExpression(expr.styleExpr, depth, indent, source)}` : '';
      return `${expr.layerType}(${name})${style}`;
    }
    case 'PathBlockExpression': {
      const body = formatStatements(expr.body as Statement[], depth + 1, indent, source);
      return `@{\n${body}\n${indent.repeat(depth)}}`;
    }
    case 'TextBlockExpression': {
      const body = formatTextBodyItems(expr.body as TextBodyItem[], depth + 1, indent, source);
      return `&{\n${body}\n${indent.repeat(depth)}}`;
    }
    default:
      return '';
  }
}
